// AskUserQuestion picker — server's SDK pause point. Renders the questions,
// collects answers, POSTs back to /chat/v2/answer to resume the agent.
//
// API_BASE preservation: uses import.meta.env.VITE_API_BASE so the demo
// works both locally (vite proxy → :8001) and in prod (same-origin).

import { CheckCircle2, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { StaggeredReveal } from "@/components/ui/animated-list";
import { getAuthHeader } from "@/lib/api";
import { cn } from "@/lib/utils";

// The agent sometimes sends options as plain strings, sometimes as
// {label, description} objects (richer pickers). Normalize at render-time.
type RawOption = string | { label: string; description?: string };

interface AskQuestion {
  id?: string;
  question: string;
  options?: RawOption[];
  multi?: boolean;
}

function optionLabel(o: RawOption): string {
  return typeof o === "string" ? o : o.label;
}

function optionDescription(o: RawOption): string | null {
  return typeof o === "string" ? null : (o.description ?? null);
}

interface AskData {
  qid: string;
  questions: AskQuestion[];
  thread_id: string;
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export function AskUserQuestionCard({ data }: { data: AskData }) {
  const initial = useMemo(
    () =>
      Object.fromEntries(
        data.questions.map((q) => [
          q.question,
          q.multi ? ([] as string[]) : "",
        ])
      ),
    [data.questions]
  );

  const [answers, setAnswers] = useState<Record<string, string | string[]>>(
    initial
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);

    // Flatten multi-select arrays for the wire format.
    const wireAnswers: Record<string, string> = {};
    for (const q of data.questions) {
      const v = answers[q.question];
      if (q.multi) {
        wireAnswers[q.question] = (Array.isArray(v) ? v : []).join(", ");
      } else {
        wireAnswers[q.question] = (typeof v === "string" ? v : "") || "";
      }
    }

    try {
      const res = await fetch(API_BASE + "/chat/v2/answer", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        body: JSON.stringify({
          thread_id: data.thread_id,
          qid: data.qid,
          answers: wireAnswers,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }
      setSubmitted(true);
    } catch (e) {
      setError((e as Error).message || "Failed to submit answer");
      console.error("AskUserQuestion submit failed", e);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        Answer submitted — waiting for the agent to continue…
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card shadow-card p-5 space-y-4">
      <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
        The agent has a question
      </p>

      <StaggeredReveal stepMs={140} className="space-y-5">
        {data.questions.map((q, qi) => (
          <div key={q.question} className="space-y-2">
            <div className="text-sm font-medium">{q.question}</div>

            {q.options && q.options.length > 0 ? (
              <StaggeredReveal
                baseDelayMs={120 + qi * 140}
                stepMs={80}
                className="flex flex-col gap-3"
              >
                {q.options.map((opt) => {
                  const value = optionLabel(opt);
                  const description = optionDescription(opt);
                  const selected = q.multi
                    ? Array.isArray(answers[q.question]) &&
                      (answers[q.question] as string[]).includes(value)
                    : answers[q.question] === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={selected}
                      title={description ?? undefined}
                      onClick={() => {
                        setAnswers((s) => {
                          if (q.multi) {
                            const cur = Array.isArray(s[q.question])
                              ? (s[q.question] as string[])
                              : [];
                            return {
                              ...s,
                              [q.question]: cur.includes(value)
                                ? cur.filter((x) => x !== value)
                                : [...cur, value],
                            };
                          }
                          return { ...s, [q.question]: value };
                        });
                      }}
                      className={cn(
                        // Editorial card — restrained surface with a
                        // subtle left-rail accent that brightens when
                        // selected. Mirrors the rest of the Inku UI
                        // (cream/off-white, paper-and-ink) instead of
                        // the previous yellow sticky-note look, which
                        // read too playful next to the workspace chrome.
                        "group relative w-full text-left rounded-lg border",
                        "px-5 py-4 transition-colors duration-200",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                        // Thin left accent rail — invisible by default,
                        // foreground-colored when selected. Subtle
                        // affordance that this is an answer choice.
                        "before:absolute before:left-0 before:top-3 before:bottom-3 before:w-[3px] before:rounded-r-full",
                        "before:transition-colors before:duration-200",
                        selected
                          ? "border-foreground bg-foreground text-background shadow-card before:bg-background/30"
                          : "border-border bg-card hover:bg-accent/40 hover:border-foreground/30 before:bg-transparent"
                      )}
                    >
                      <div className="flex flex-col gap-1.5">
                        <p className="font-medium text-sm leading-snug">
                          {value}
                        </p>
                        {description && (
                          <p
                            className={cn(
                              "text-xs leading-relaxed",
                              selected
                                ? "text-background/75"
                                : "text-muted-foreground"
                            )}
                          >
                            {description}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </StaggeredReveal>
            ) : (
              <input
                type="text"
                value={
                  typeof answers[q.question] === "string"
                    ? (answers[q.question] as string)
                    : ""
                }
                onChange={(e) =>
                  setAnswers((s) => ({ ...s, [q.question]: e.target.value }))
                }
                placeholder="Type your answer…"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              />
            )}

            {qi < data.questions.length - 1 && (
              <div className="h-px bg-border/50 mt-3" />
            )}
          </div>
        ))}
      </StaggeredReveal>

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={submit} disabled={submitting}>
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </div>
  );
}
